// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash/fp';
import * as PxcWidget from '@playchip/embeddable-widget';
import { GET_ACCESS_TOKEN_STARTED, GET_ACCESS_TOKEN_FINISHED } from 'app/constants/actionTypes';
import { submitPxcEntries } from 'app/actions/challengeActions';
import { submitMultiplePxcEntries } from 'app/actions/compatibleChallengesActions';
import { submitMultiplePxcEntries as submitMultiplePxcLineupEntries } from 'app/actions/myTeamsActions';
import { pxcSignIn, userSignOut } from '../actions/userActions';
import PxcContext from '../contexts/PxcContext';
import pxcWidgetConsts from '../constants/pxcWidget';

type Props = {
  isEnabled: boolean,
  isSignedIn: boolean,
  partnerId?: string,
  children: React.Node,
  currentUserEmail?: string,
  getAccessTokenStarted: () => void,
  getAccessTokenFinished: () => void,
  doLogin: () => void,
  doLogout: () => void,
  submitEntries: Object => void,
  submitMultipleEntries: Object => void,
  submitMultipleLineupEntries: Object => void,
  url?: string
};

type State = {
  WidgetContext?: PxcWidget.PlayXchangeContext,
  partnerAuthorised?: boolean,
  batchId?: string,
  transactions?: Array<*>
};

type AuthenticationEvent = {
  source: string,
  type: string,
  accessToken?: string,
  authenticated?: boolean,
  email?: string
};

class PxcProvider extends React.Component<Props> {
  props: Props;

  state: State = {
    PxcContext: undefined,
    batchId: undefined,
    transactions: [],
    partnerAuthorised: undefined
  };

  static defaultProps = {
    currentUserEmail: '',
    partnerId: undefined,
    url: undefined
  };

  async componentDidMount() {
    const { isEnabled, partnerId, url } = this.props;
    if (!isEnabled) {
      return;
    }

    const ctx = await PxcWidget.init({
      partnerId,
      url
    });

    if (ctx) {
      this.setState({
        WidgetContext: ctx
      });
    }

    window.addEventListener('message', this.handleMessage);
  }

  componentWillUnmount() {
    const { isEnabled } = this.props;
    if (!isEnabled) {
      return;
    }
    window.removeEventListener('message', this.handleMessage);
  }

  submitFunc = ({ batchId, transactions }) => {
    const { submitEntries, submitMultipleEntries, submitMultipleLineupEntries } = this.props;

    if (get('metadata.lineups', transactions[0])) {
      submitMultipleLineupEntries({ batchId, transactions });
      return;
    }
    if (get('metadata.multi', transactions[0])) {
      submitMultipleEntries({ batchId, transactions });
      return;
    }
    submitEntries({ batchId, transactions });
  };

  handleMessage = async event => {
    const eventData: AuthenticationEvent = event.data;
    const { WidgetContext } = this.state;
    const {
      currentUserEmail,
      getAccessTokenStarted,
      getAccessTokenFinished,
      isSignedIn,
      doLogin,
      doLogout
    } = this.props;

    if (eventData.type === pxcWidgetConsts.AUTHENTICATION) {
      const { authenticated, partnerAuthorised, accessToken, action } = eventData;
      const email = get('email', eventData) || '';

      if (
        currentUserEmail &&
        (currentUserEmail.toLowerCase() !== email.toLowerCase() || !partnerAuthorised)
      ) {
        doLogout();
      }

      if (
        authenticated &&
        partnerAuthorised &&
        currentUserEmail.toLowerCase() !== email.toLowerCase()
      ) {
        if (!accessToken && action !== 'GetAccessTokenFailed') {
          getAccessTokenStarted();
          const { accessToken: token } = await WidgetContext.getPartnerToken();

          if (token) {
            doLogin(token);
          }

          if (isSignedIn) {
            getAccessTokenFinished();
          }
        }
      }

      return;
    }

    if (eventData.type === pxcWidgetConsts.TRANSACTION) {
      if (eventData.status === 'IN_FLIGHT') {
        const { batchId, transactions } = eventData;

        this.submitFunc({ batchId, transactions });
      }
    }
  };

  setSubmitFunc = submitFunc => {
    this.submitFunc = submitFunc;
  };

  requestLogin = () => {
    const { WidgetContext } = this.state;
    WidgetContext.getPartnerToken();
  };

  previewTransfer = requestTransferOptions => {
    const { WidgetContext } = this.state;
    // resetTransferData();
    WidgetContext.previewTransfer(requestTransferOptions);
  };

  getPartnerId = () => {
    const { partnerId } = this.props;
    return partnerId;
  };

  render() {
    const { children, isEnabled } = this.props;

    return (
      <PxcContext.Provider
        value={{
          ...this.state,
          isEnabled,
          getPartnerId: this.getPartnerId,
          login: this.requestLogin,
          previewTransfer: this.previewTransfer,
          setSubmitFunc: this.setSubmitFunc
        }}
      >
        {children}
      </PxcContext.Provider>
    );
  }
}

const mapStateToProps = state => ({
  isEnabled: !!get('enablePxc', state.settings),
  isSignedIn: get('user.isSignedIn', state),
  partnerId: get('pxcPartnerId', state.settings),
  url: get('pxcUrl', state.settings),
  currentUserEmail: get('user.currentUser.email', state) || ''
});

const mapDispatchToProps = dispatch => ({
  doLogin: token => dispatch(pxcSignIn(token)),
  doLogout: () => dispatch(userSignOut()),
  submitEntries: ({ batchId, transactions }) =>
    dispatch(submitPxcEntries({ batchId, transactions })),
  submitMultipleEntries: ({ batchId, transactions }) =>
    dispatch(submitMultiplePxcEntries({ batchId, transactions })),
  submitMultipleLineupEntries: ({ batchId, transactions }) =>
    dispatch(submitMultiplePxcLineupEntries({ batchId, transactions })),
  getAccessTokenStarted: () => dispatch({ type: GET_ACCESS_TOKEN_STARTED }),
  getAccessTokenFinished: () => dispatch({ type: GET_ACCESS_TOKEN_FINISHED })
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PxcProvider);
